// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this file.
/**
 * Arrears Page - Empathetic Client Outreach Dashboard
 *
 * Purpose: Help brokers identify clients with past-due trail payments for empathetic outreach.
 * Business Value: ~1-2% of loans may be in arrears - creates goodwill conversation opportunities.
 *
 * Data Source: BigQuery raw_commissions where payment_type_original = 'arrears_commission'
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { resolveLoanBookSelection, getDisplayLoanBooks } from '../../utils/loanBookResolution';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useUserData } from '../../hooks/useUserData';
import { useCompanyData } from '../../hooks/useCompanyData';
import { useArrears, BrokerBreakdown } from '../../hooks/useArrears';
import { useCompanyDataYearRange } from '../../hooks/useCompanyDataYearRange';
import { useLoanContacts } from '../../hooks/useLoanContacts';
import type { MutateContactInput } from '../../hooks/useLoanContacts';
import {
  useTrailRiskStatusForRefs,
  refKey,
  type TrailRiskRef,
} from '../../hooks/useTrailRiskStatusForRefs';
import ContactTimeline from '../../components/contacts/ContactTimeline';
import { TrailRiskFollowUp } from '../../components/contacts/TrailRiskFollowUp';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { formatCurrency } from '../../utils/formatters';
import { ChevronDown, ChevronUp, MessageSquare, Clock } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';

interface Office {
  id: string;
  name: string;
}

/**
 * The 'YYYY-MM' that keys an arrears trail-risk event for one payment row.
 *
 * The materializer groups arrears by `FORMAT_DATE('%Y-%m', invoiced_date)`
 * (queries/shared/trailRiskMaterializerSql.ts), so the ref must be the INVOICE
 * month. `getArrearsReport.ts` now returns exactly that as `invoiceMonth`.
 *
 * An earlier version of this recovered the month by regex-parsing `paymentId`,
 * which is a composite key rather than a date field. That worked, but it coupled
 * a chip on this page to the internal FORMAT of an id built elsewhere: change the
 * separator or the date format in that CONCAT and the chips would quietly stop
 * matching, with no compiler or test to catch it. The server column is the same
 * information without the coupling.
 *
 * Returns null when the row has no invoice date, in which case no chip renders.
 */
const arrearsPeriodMonth = (payment: { invoiceMonth?: string | null }): string | null => {
  const m = payment?.invoiceMonth || '';
  return /^\d{4}-\d{2}$/.test(m) ? m : null;
};

/**
 * Format percentage - show 1 decimal for values between 0-1%, otherwise 0 decimals
 * Prevents misleading "0%" when there are actually arrears
 */
const formatPercent = (value: number): string => {
  if (value === 0) return '0%';
  if (value > 0 && value < 1) return `${value.toFixed(1)}%`;
  return `${Math.round(value)}%`;
};

/**
 * Safely formats date to display string
 */
const formatDate = (dateValue: any): string => {
  if (!dateValue) return 'N/A';
  try {
    const date = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch (error) {
    return 'N/A';
  }
};

const ArrearsPage: React.FC = () => {
  const { userData } = useUserData();
  const { companyData } = useCompanyData();
  const { userVisibleOffices, loanBooks = [] } = useOutletContext<{ userVisibleOffices: Office[]; loanBooks: ResolvedLoanBook[] }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkLoanId = searchParams.get('loanId');

  // Deep-link state
  const deepLinkRowRef = useRef<HTMLTableRowElement | null>(null);
  const deepLinkProcessed = useRef(false);
  const [highlightedLoanId, setHighlightedLoanId] = useState<string | null>(null);
  const [scrollTrigger, setScrollTrigger] = useState(0);

  // Dynamic year range from BigQuery
  const { earliestYear, latestYear } = useCompanyDataYearRange({
    companyId: userData?.companyId,
  });

  // Filter state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedBroker, setSelectedBroker] = useState('');
  const [selectedOffice, setSelectedOffice] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('all');

  const resolvedParams = useMemo(() =>
      resolveLoanBookSelection(selectedTemplate, loanBooks),
      [selectedTemplate, loanBooks]
  );

  const [sortBy, setSortBy] = useState('arrearsDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  // Contact tracking state (canonical shared store — Part B)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [savingContact, setSavingContact] = useState<Record<string, boolean>>({});

  // Canonical loan-keyed contact store (dual-reads legacy arrears_contacts + others)
  const { contactsByLoan, mutateContact } = useLoanContacts(userData?.companyId);


  // Active company brokers for contact "Made By" dropdown
  // Broker family roles: only the logged-in broker. Other roles: all active brokers.
  const [contactBrokerList, setContactBrokerList] = useState<{ id: string; name: string }[]>([]);

  // Fetch arrears data from BigQuery
  const {
    arrearsLoans: allArrears,
    loading,
    error,
    totalArrearsPayments,
    totalArrearsAmountCents,
    totalLoanBalanceCents,
    uniqueLoanCount,
    totalBookLoanCount,
    arrearsPercentByCount,
    arrearsPercentByBalance,
    monthlyTrend,
    brokerBreakdown,
    refetch,
  } = useArrears({
    companyId: userData?.companyId || '',
    year: selectedYear,
    yearType: 'calendar',
    brokerId: userData?.role === 'broker' ? userData?.brokerId : undefined,
    officeId: selectedOffice || undefined,
    templateId: resolvedParams.templateId,
    entityCode: resolvedParams.entityCode,
  });

  // Dynamic year range
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const dynamicLatestYear = Math.max(latestYear ?? currentYear, currentYear);
    const dynamicEarliestYear = earliestYear ?? (currentYear - 4);
    const dynamicYears: number[] = [];
    for (let year = dynamicLatestYear; year >= dynamicEarliestYear; year--) {
      dynamicYears.push(year);
    }
    setAvailableYears(dynamicYears);
  }, [earliestYear, latestYear]);

  // Fetch active company brokers for contact "Made By" dropdown
  // Broker role: only the logged-in broker (skip Firestore read)
  // Other roles: all active brokers from Firestore
  useEffect(() => {
    if (!userData?.companyId) return;

    if (userData?.role === 'broker' || userData?.role === 'broker_assistant') {
      const brokerName = userData.commissionDataName || userData.displayName || '';
      if (brokerName) {
        setContactBrokerList([{ id: 'self', name: brokerName }]);
      }
      return;
    }

    const brokersQuery = query(
      collection(db, `companies/${userData.companyId}/brokers`)
    );

    const unsubscribe = onSnapshot(
      brokersQuery,
      (snapshot) => {
        const list: { id: string; name: string }[] = [];
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.mergedInto) return;
          if ((data.status || 'active') !== 'active') return;
          if (data.currentName && typeof data.currentName === 'string') {
            list.push({ id: docSnap.id, name: data.currentName });
          }
        });
        list.sort((a, b) => a.name.localeCompare(b.name));
        setContactBrokerList(list);
      },
      (err) => {
        console.error('[ArrearsPage] Error fetching brokers for contact dropdown:', err);
      }
    );

    return () => unsubscribe();
  }, [userData?.companyId, userData?.role, userData?.commissionDataName, userData?.displayName]);

  // Extract broker list for filter
  const brokers = useMemo(() => {
    if (brokerBreakdown && brokerBreakdown.length > 0) {
      return Array.from(new Set(
        brokerBreakdown.map((b: BrokerBreakdown) => b.brokerDisplay).filter(Boolean)
      )).sort();
    }
    if (!allArrears || allArrears.length === 0) return [];
    return Array.from(new Set(allArrears.map((d) => d.broker).filter(Boolean))).sort();
  }, [allArrears, brokerBreakdown]);

  // Chart data for monthly trend (grouped by loan)
  const monthlyChartData = useMemo(() => {
    const chartArrears = selectedBroker
      ? allArrears.filter((record) => record.broker === selectedBroker)
      : allArrears;

    const monthlyData = Array.from({ length: 12 }, (_, monthIndex) => {
      const monthRecords = chartArrears.filter((record) => {
        const date = record.latestArrearsDate;
        return date.getMonth() === monthIndex && date.getFullYear() === selectedYear;
      });

      const monthName = new Date(selectedYear, monthIndex, 1).toLocaleString('en-AU', { month: 'short' });

      return {
        month: monthName,
        count: monthRecords.length,
        amount: monthRecords.reduce((sum, r) => sum + r.totalArrearsCents, 0) / 100,
      };
    });

    return monthlyData;
  }, [allArrears, selectedBroker, selectedYear]);

  // Helper to safely get timestamp from date
  const getTimestamp = (date: Date | null | undefined): number => {
    if (!date) return 0;
    const time = date.getTime();
    return isNaN(time) ? 0 : time;
  };

  // Sorted and filtered display data (grouped by loan)
  const displayArrears = useMemo(() => {
    let filtered = selectedBroker
      ? allArrears.filter((record) => record.broker === selectedBroker)
      : allArrears;

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortBy) {
        case 'customerName':
          aValue = (a.customerName || '').toLowerCase();
          bValue = (b.customerName || '').toLowerCase();
          break;
        case 'loanId':
          aValue = a.loanId || '';
          bValue = b.loanId || '';
          break;
        case 'broker':
          aValue = (a.broker || '').toLowerCase();
          bValue = (b.broker || '').toLowerCase();
          break;
        case 'lender':
          aValue = (a.lender || '').toLowerCase();
          bValue = (b.lender || '').toLowerCase();
          break;
        case 'settlementDate':
          aValue = getTimestamp(a.settlementDate);
          bValue = getTimestamp(b.settlementDate);
          break;
        case 'arrearsDate':
          aValue = getTimestamp(a.latestArrearsDate);
          bValue = getTimestamp(b.latestArrearsDate);
          break;
        case 'loanBalance':
          aValue = a.loanBalanceCents || 0;
          bValue = b.loanBalanceCents || 0;
          break;
        case 'amount':
          aValue = a.totalArrearsCents || 0;
          bValue = b.totalArrearsCents || 0;
          break;
        case 'arrearsCount':
          aValue = a.arrearsPaymentCount || 1;
          bValue = b.arrearsPaymentCount || 1;
          break;
        default:
          aValue = getTimestamp(a.latestArrearsDate);
          bValue = getTimestamp(b.latestArrearsDate);
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      // Numeric comparison
      const numA = Number(aValue) || 0;
      const numB = Number(bValue) || 0;
      return sortDirection === 'asc' ? numA - numB : numB - numA;
    });

    return sorted;
  }, [allArrears, selectedBroker, sortBy, sortDirection]);

  // Trail-risk follow-up status (server-only client_events, RBAC-scoped),
  // resolved PER OCCURRENCE. Arrears events are keyed (loan, invoice month) and
  // this report already renders one row per payment period, so a loan in arrears
  // in both June and July gets TWO chips and two events. The old per-loan lookup
  // collapsed them and could close June from the July row.
  const trailRiskRefs = useMemo<TrailRiskRef[]>(() => {
    const seen = new Set<string>();
    const out: TrailRiskRef[] = [];
    for (const loan of displayArrears) {
      for (const payment of loan.payments || []) {
        const periodMonth = arrearsPeriodMonth(payment);
        if (!periodMonth || !loan.loanId) continue;
        const ref: TrailRiskRef = {
          loanId: loan.loanId,
          eventType: 'arrears_commission',
          periodMonth,
        };
        const k = refKey(ref);
        // Several payment ROWS (different run numbers) can share one invoice
        // month; they are one event.
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(ref);
      }
    }
    return out;
  }, [displayArrears]);
  const {
    statusByRef: trailRiskByRef,
    error: trailRiskError,
    refetch: refetchTrailRisk,
  } = useTrailRiskStatusForRefs(userData?.companyId, trailRiskRefs);

  // Handle sort
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
  };

  // Deep-link: navigate to row, expand, and scroll into view
  useEffect(() => {
    if (!deepLinkLoanId || loading || displayArrears.length === 0 || deepLinkProcessed.current) return;

    const targetIndex = displayArrears.findIndex(r => r.loanFingerprint === deepLinkLoanId);
    if (targetIndex === -1) {
      setSearchParams({}, { replace: true });
      deepLinkProcessed.current = true;
      return;
    }

    setExpandedRows(prev => new Set(prev).add(deepLinkLoanId));
    setHighlightedLoanId(deepLinkLoanId);
    setSearchParams({}, { replace: true });
    deepLinkProcessed.current = true;
    setScrollTrigger(prev => prev + 1);
  }, [deepLinkLoanId, loading, displayArrears, setSearchParams]);

  // Deep-link: scroll to target row after render
  useEffect(() => {
    if (scrollTrigger === 0) return;
    const timer = setTimeout(() => {
      deepLinkRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
    return () => clearTimeout(timer);
  }, [scrollTrigger]);

  // Row expansion
  const toggleRow = useCallback((loanFingerprint: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(loanFingerprint)) {
        newSet.delete(loanFingerprint);
      } else {
        newSet.add(loanFingerprint);
      }
      return newSet;
    });
  }, []);

  // Audit-grade write via mutateLoanContact, wrapped to drive the per-loan
  // saving indicator. The shared <ContactTimeline> passes explicit create/
  // update/delete intent.
  const handleMutateContact = useCallback(async (input: MutateContactInput) => {
    setSavingContact(prev => ({ ...prev, [input.loanId]: true }));
    try {
      return await mutateContact(input);
    } finally {
      setSavingContact(prev => ({ ...prev, [input.loanId]: false }));
    }
  }, [mutateContact]);

  // Count contacted loans (derived from canonical contact store, keyed by loan_id)
  const contactedCount = useMemo(() => {
    return displayArrears.filter(loan => {
      const entries = contactsByLoan[loan.loanId];
      return entries && entries.length > 0;
    }).length;
  }, [displayArrears, contactsByLoan]);

  // RBAC: Show office filter only for admins with multiple offices
  const isBrokerFamilyRole = userData?.role === 'broker' || userData?.role === 'broker_assistant';
  const showOfficeFilter = !isBrokerFamilyRole && userVisibleOffices && userVisibleOffices.length > 1;
  const showBrokerFilter = !isBrokerFamilyRole;

  // Loading state
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-gray-400">Loading arrears data...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded">
          Error loading arrears data: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Clock}
        title="Arrears"
        subtitle="Track loans with past-due payments and manage empathetic client outreach"
        onRefresh={() => refetch()}
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        {/* Year Filter */}
        <div>
          <label className="block text-xs text-gray-400 mb-1">Year</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 text-sm"
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {/* Office Filter */}
        {showOfficeFilter && (
          <div>
            <label className="block text-xs text-gray-400 mb-1">Office</label>
            <select
              value={selectedOffice}
              onChange={(e) => setSelectedOffice(e.target.value)}
              className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 text-sm"
            >
              <option value="">All Offices</option>
              {userVisibleOffices.map((office) => (
                <option key={office.id} value={office.id}>{office.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Broker Filter */}
        {showBrokerFilter && (
          <div>
            <label className="block text-xs text-gray-400 mb-1">Broker</label>
            <select
              value={selectedBroker}
              onChange={(e) => setSelectedBroker(e.target.value)}
              className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 text-sm"
            >
              <option value="">All Brokers</option>
              {brokers.map((broker) => (
                <option key={broker} value={broker}>{broker}</option>
              ))}
            </select>
          </div>
        )}

        {/* Loan Book Filter */}
        {loanBooks.length > 0 && (
          <div>
            <label className="block text-xs text-gray-400 mb-1">Loan Book</label>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 text-sm"
            >
              <option value="all">All Loan Books</option>
              {getDisplayLoanBooks(loanBooks).map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">Loans in Arrears</div>
          <div className="text-2xl font-bold text-white mt-1">{(uniqueLoanCount ?? 0).toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">
            {totalArrearsPayments ?? 0} arrears payments
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">% of Book in Arrears</div>
          <div className="text-2xl font-bold text-orange-400 mt-1">
            {formatPercent(arrearsPercentByCount ?? 0)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {uniqueLoanCount ?? 0} of {totalBookLoanCount ?? 0} loans
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">Total Arrears Amount</div>
          <div className="text-2xl font-bold text-orange-400 mt-1">
            {formatCurrency(totalArrearsAmountCents ?? 0)}
          </div>
          <div className="text-xs text-gray-500 mt-1">Commission in arrears</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">Loan Balance at Risk</div>
          <div className="text-2xl font-bold text-blue-400 mt-1">
            {formatCurrency(totalLoanBalanceCents ?? 0)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {formatPercent(arrearsPercentByBalance ?? 0)} of book balance
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">Contacted</div>
          <div className="text-2xl font-bold text-green-400 mt-1">
            {contactedCount} / {displayArrears.length}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {displayArrears.length > 0
              ? `${((contactedCount / displayArrears.length) * 100).toFixed(0)}% contacted`
              : 'No arrears to contact'}
          </div>
        </div>
      </div>

      {/* Monthly Trend Chart */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <h3 className="text-white font-semibold mb-4">Monthly Arrears Trend</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="month" stroke="#9CA3AF" tick={{ fill: '#9CA3AF' }} />
              <YAxis stroke="#9CA3AF" tick={{ fill: '#9CA3AF' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                }}
                labelStyle={{ color: '#F9FAFB' }}
                formatter={(value: number, name: string) => {
                  if (name === 'amount') return [`$${value.toLocaleString()}`, 'Amount'];
                  return [value, 'Count'];
                }}
              />
              <Bar dataKey="count" fill="#f97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Arrears Table */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-white font-semibold">
            Arrears Loans ({displayArrears.length})
          </h3>
          <p className="text-gray-400 text-sm mt-1">
            Click a row to expand and log contact attempts
          </p>
        </div>

        {displayArrears.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            No arrears found for the selected filters
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-900 text-gray-400">
                <tr>
                  <th className="px-4 py-3 text-left w-8"></th>
                  <th
                    className="px-4 py-3 text-left cursor-pointer hover:text-white"
                    onClick={() => handleSort('customerName')}
                  >
                    Customer {sortBy === 'customerName' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="px-4 py-3 text-left cursor-pointer hover:text-white"
                    onClick={() => handleSort('loanId')}
                  >
                    Loan ID {sortBy === 'loanId' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="px-4 py-3 text-left cursor-pointer hover:text-white"
                    onClick={() => handleSort('broker')}
                  >
                    Broker {sortBy === 'broker' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="px-4 py-3 text-left cursor-pointer hover:text-white"
                    onClick={() => handleSort('lender')}
                  >
                    Lender {sortBy === 'lender' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="px-4 py-3 text-left cursor-pointer hover:text-white"
                    onClick={() => handleSort('settlementDate')}
                  >
                    Settlement {sortBy === 'settlementDate' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="px-4 py-3 text-left cursor-pointer hover:text-white"
                    onClick={() => handleSort('arrearsDate')}
                  >
                    Latest Arrears {sortBy === 'arrearsDate' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="px-4 py-3 text-right cursor-pointer hover:text-white"
                    onClick={() => handleSort('loanBalance')}
                  >
                    Loan Balance {sortBy === 'loanBalance' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="px-4 py-3 text-right cursor-pointer hover:text-white"
                    onClick={() => handleSort('amount')}
                  >
                    Total Arrears {sortBy === 'amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="px-4 py-3 text-center cursor-pointer hover:text-white"
                    onClick={() => handleSort('arrearsCount')}
                  >
                    # {sortBy === 'arrearsCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {displayArrears.map((loan) => {
                  const isExpanded = expandedRows.has(loan.loanFingerprint);
                  const loanEntries = contactsByLoan[loan.loanId] || [];
                  const hasContacts = loanEntries.length > 0;

                  return (
                    <React.Fragment key={loan.id}>
                      {/* Main Row */}
                      <tr
                        ref={highlightedLoanId === loan.loanFingerprint ? deepLinkRowRef : undefined}
                        className={`hover:bg-gray-700/50 cursor-pointer transition ${highlightedLoanId === loan.loanFingerprint ? 'ring-2 ring-blue-500/50 bg-blue-900/20' : ''}`}
                        onClick={() => toggleRow(loan.loanFingerprint)}
                      >
                        <td className="px-4 py-3">
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </td>
                        <td className="px-4 py-3 text-white">{loan.customerName || 'N/A'}</td>
                        <td className="px-4 py-3 text-gray-300">
                          <span className="inline-flex items-center gap-2">
                            {loan.loanId}
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">ARREARS</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-300">{loan.broker}</td>
                        <td className="px-4 py-3 text-gray-300">{loan.lender || 'N/A'}</td>
                        <td className="px-4 py-3 text-gray-300">{formatDate(loan.settlementDate)}</td>
                        <td className="px-4 py-3 text-gray-300">{formatDate(loan.latestArrearsDate)}</td>
                        <td className="px-4 py-3 text-right text-gray-300">
                          {formatCurrency(loan.loanBalanceCents)}
                        </td>
                        <td className="px-4 py-3 text-right text-orange-400 font-medium">
                          {formatCurrency(loan.totalArrearsCents)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {loan.arrearsPaymentCount > 1 ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 bg-orange-900/50 text-orange-300 rounded-full text-xs font-medium">
                              {loan.arrearsPaymentCount}
                            </span>
                          ) : (
                            <span className="text-gray-500">1</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {hasContacts ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-900 text-green-300 rounded-full text-xs">
                              <MessageSquare className="w-3 h-3" />
                              Contacted
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-700 text-gray-400 rounded-full text-xs">
                              Pending
                            </span>
                          )}
                        </td>
                      </tr>

                      {/* Expanded Contact Section */}
                      {isExpanded && (
                        <tr className="bg-gray-900/50">
                          <td colSpan={11} className="px-4 py-4">
                            <div className="space-y-4">
                              {/* Arrears Payment Breakdown */}
                              {loan.payments && loan.payments.length > 0 && (
                                <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                                  <div className="px-4 py-2 bg-gray-900 border-b border-gray-700">
                                    <h4 className="text-white font-medium text-sm">
                                      Arrears Payment History ({loan.payments.length} payments)
                                    </h4>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead className="bg-gray-900/50 text-gray-400">
                                        <tr>
                                          <th className="px-4 py-2 text-left">Date</th>
                                          <th className="px-4 py-2 text-left">Period</th>
                                          <th className="px-4 py-2 text-center">Run</th>
                                          <th className="px-4 py-2 text-right">Loan Balance</th>
                                          <th className="px-4 py-2 text-right">Arrears Amount</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-700">
                                        {loan.payments.map((payment, idx) => (
                                          <tr key={payment.paymentId || idx} className="hover:bg-gray-700/30">
                                            <td className="px-4 py-2 text-gray-300">
                                              {formatDate(payment.arrearsDate)}
                                            </td>
                                            <td className="px-4 py-2 text-gray-300">
                                              {payment.periodMonth || 'N/A'}
                                            </td>
                                            <td className="px-4 py-2 text-center text-gray-300">
                                              {payment.runNumber ? `RUN ${payment.runNumber}` : '-'}
                                            </td>
                                            <td className="px-4 py-2 text-right text-gray-300">
                                              {formatCurrency(payment.loanBalanceCents)}
                                            </td>
                                            <td className="px-4 py-2 text-right text-orange-400 font-medium">
                                              {formatCurrency(payment.commissionCents)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                      <tfoot className="bg-gray-900/50">
                                        <tr>
                                          <td colSpan={4} className="px-4 py-2 text-right text-gray-400 font-medium">
                                            Total Arrears:
                                          </td>
                                          <td className="px-4 py-2 text-right text-orange-400 font-bold">
                                            {formatCurrency(loan.totalArrearsCents)}
                                          </td>
                                        </tr>
                                      </tfoot>
                                    </table>
                                  </div>
                                </div>
                              )}

                              {/* Trail-risk follow-up, ONE PER ARREARS PERIOD: an event is
                                  keyed (loan, invoice month), so each month this loan was in
                                  arrears is followed up and closed separately. */}
                              {userData?.companyId && (() => {
                                const periods: string[] = [];
                                for (const payment of loan.payments || []) {
                                  const periodMonth = arrearsPeriodMonth(payment);
                                  if (periodMonth && !periods.includes(periodMonth)) periods.push(periodMonth);
                                }
                                const rows = periods
                                  .map((periodMonth) => ({
                                    periodMonth,
                                    status: trailRiskByRef.get(
                                      refKey({
                                        loanId: loan.loanId,
                                        eventType: 'arrears_commission',
                                        periodMonth,
                                      }),
                                    ),
                                  }))
                                  // Nothing to say and no failure to report: stay silent.
                                  .filter((r) => r.status || trailRiskError);
                                if (rows.length === 0) return null;
                                return (
                                  <div className="mb-3 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                                    {rows.map((r) => (
                                      <div key={r.periodMonth} className="flex items-center gap-2">
                                        <span className="text-xs text-gray-400 w-16 shrink-0">{r.periodMonth}</span>
                                        <TrailRiskFollowUp
                                          companyId={userData.companyId}
                                          status={r.status}
                                          statusUnavailable={Boolean(trailRiskError)}
                                          onChanged={refetchTrailRisk}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}

                              {/* Unified contact timeline (canonical shared store) */}
                              <div onClick={(e) => e.stopPropagation()}>
                                <ContactTimeline
                                  companyId={userData?.companyId || ''}
                                  loanId={loan.loanId}
                                  customerName={loan.customerName}
                                  loanBroker={loan.broker}
                                  sourcePage="Arrears"
                                  entries={loanEntries}
                                  availableBrokers={contactBrokerList}
                                  currentUser={{
                                    uid: userData?.uid,
                                    displayName: userData?.commissionDataName || userData?.displayName,
                                    email: userData?.email,
                                    role: userData?.role,
                                  }}
                                  onMutate={handleMutateContact}
                                  isSaving={savingContact[loan.loanId]}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArrearsPage;
